import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Users, Zap, CreditCard, QrCode, CheckCircle, Star, Send, Shield, Eye } from 'lucide-react';
import { useAuth } from '../context/CompleteAuthContext';
import CompleteAuthModal from './CompleteAuthModal';
import VerificationBanner from './VerificationBanner';
import PayoLogo from './PayoLogo';

const Landing = ({ onGetStarted }) => {
  const { isAuthenticated, user, resendVerification } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin');

  const handleGetStarted = () => {
    if (isAuthenticated) {
      onGetStarted();
    } else {
      setAuthModalMode('signup');
      setShowAuthModal(true);
    }
  };

  const handleSignIn = () => {
    setAuthModalMode('signin');
    setShowAuthModal(true);
  };

  const features = [
    {
      icon: Zap,
      title: 'Instant Quick Splits',
      description: 'Create a split in seconds. Share a link or QR code. Get paid without chasing anyone.'
    },
    {
      icon: Eye,
      title: 'Real-Time Tracking',
      description: 'See who has paid and who hasn\'t — live. No more guessing or awkward follow-ups.'
    },
    {
      icon: QrCode,
      title: 'Share by Link or QR',
      description: 'Payers scan a QR code or tap a link. No app download required to pay.'
    },
    {
      icon: CreditCard,
      title: 'Flexible Payment Options',
      description: 'Payers choose how to settle — card, bank transfer, or pay another way.'
    },
  ];

  const testimonials = [
    {
      name: 'Sarah',
      role: 'Trip organizer',
      content: 'I created a Quick Split for our Airbnb and everyone paid within an hour. No more chasing Venmo requests.',
      rating: 5,
      initials: 'S',
      color: 'bg-blue-100 text-blue-700',
    },
    {
      name: 'Mike',
      role: 'Roommate',
      content: 'We use Payo for every group dinner. The QR code makes it so easy — just scan and pay.',
      rating: 5,
      initials: 'M',
      color: 'bg-green-100 text-green-700',
    },
    {
      name: 'Lisa',
      role: 'Team lead',
      content: 'Organized a team outing and had everyone settled up before we even left the restaurant.',
      rating: 5,
      initials: 'L',
      color: 'bg-amber-100 text-amber-700',
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header — unified layout: logo left, actions right */}
      <header className="px-6 h-16">
        <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity" data-testid="payo-home-link-landing">
            <PayoLogo size="md" />
          </a>
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <Button onClick={onGetStarted} className="bg-blue-600 hover:bg-blue-700">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button variant="ghost" className="text-gray-600 hover:text-blue-600" onClick={handleSignIn}>
                  Sign In
                </Button>
                <Button onClick={handleGetStarted} className="bg-blue-600 hover:bg-blue-700">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 lg:py-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Split expenses.
                <span className="block text-blue-600">Get paid instantly.</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Create a Quick Split, share a link or QR code, and track payments in real time. No more chasing people for money.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={handleGetStarted}
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-base font-semibold"
              >
                <Zap className="w-5 h-5 mr-2" />
                Start a Quick Split
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={handleSignIn}
                size="lg"
                variant="outline"
                className="px-8 py-4 text-base"
              >
                Sign In
              </Button>
            </div>

            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Shield className="w-4 h-4 text-green-500" />
              <span>Payments powered by Stripe</span>
            </div>
          </div>

          {/* Demo card */}
          <div className="relative">
            <div className="relative z-10">
              <Card className="bg-white shadow-2xl border-0 overflow-hidden">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Quick Split</p>
                        <h3 className="text-lg font-semibold text-gray-900">Dinner at Nobu</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Total</p>
                        <p className="text-xl font-bold text-gray-900">$240.00</p>
                      </div>
                    </div>

                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <p className="text-xs text-gray-500 text-center">3 of 4 paid</p>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">A</div>
                          <span className="text-sm font-medium text-gray-900">Alex</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-green-600 font-medium">Paid</span>
                          <span className="text-sm font-semibold text-gray-900">$60.00</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">J</div>
                          <span className="text-sm font-medium text-gray-900">Jordan</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-green-600 font-medium">Paid</span>
                          <span className="text-sm font-semibold text-gray-900">$60.00</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">T</div>
                          <span className="text-sm font-medium text-gray-900">Taylor</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-green-600 font-medium">Paid</span>
                          <span className="text-sm font-semibold text-gray-900">$60.00</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">S</div>
                          <span className="text-sm font-medium text-gray-900">Sam</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-amber-600 font-medium">Unpaid</span>
                          <span className="text-sm font-semibold text-gray-900">$60.00</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-200 rounded-full opacity-20 animate-pulse"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-green-200 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Built for trips, dinners, and groups
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stop chasing payments. Start a Quick Split and track who's paid in real time.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={feature.title} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-6 py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">People love splitting with Payo</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, index) => (
              <Card key={t.name} className="border-0 shadow-lg">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">"{t.content}"</p>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${t.color}`}>
                      {t.initials}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white">
            Ready to split your next expense?
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Create a Quick Split in seconds. Share the link. Get paid in real time.
          </p>
          <Button 
            onClick={handleGetStarted}
            size="lg" 
            className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-base font-semibold"
          >
            {isAuthenticated ? 'Go to Dashboard' : 'Start Your First Split'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center">
              <span className="text-xl font-bold text-white">Payo</span>
            </div>
            <div className="flex items-center space-x-8 text-gray-400 text-sm">
              <span>Privacy Policy</span>
              <span>Terms of Service</span>
              <span>Support</span>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <p>&copy; 2025 Payo Inc. All Rights Reserved</p>
          </div>
        </div>
      </footer>

      {/* Email Verification Banner */}
      {isAuthenticated && user && !user.email_verified && (
        <div className="fixed top-0 left-0 right-0 z-50 p-4">
          <VerificationBanner user={user} onResend={resendVerification} />
        </div>
      )}

      <CompleteAuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authModalMode}
      />
    </div>
  );
};

export default Landing;
